import { useEffect, useRef, useState, FC, ReactNode } from 'react'
import styled from 'styled-components'
import { useFormContext, Controller } from 'react-hook-form'
import get from 'lodash/get'

import Input from 'react-phone-number-input/input'
import en from 'react-phone-number-input/locale/en.json'
import { getCountries, getCountryCallingCode } from 'react-phone-number-input'
import { isValidNumber, parsePhoneNumber } from 'libphonenumber-js'

import { useGetUserCountryAndStripeKeyQuery } from 'hooks/useQueries'

import ChevronDown from '../icons/ChevronDown2'
import ValidIcon from '../icons/Valid'

// @ts-ignore - JS module without types
import { InputGroup, SelectField } from '../form'
import FieldWrapper from './Field'

export const validPhoneNumber = (value: string | undefined) => {
  if (value && !isValidNumber(value)) {
    return 'Not a valid phone number'
  }
  return undefined
}

const PhoneInputWrapper = styled.div<{
  error?: boolean
  success?: boolean
  rounded?: boolean
}>`
  position: relative;
  width: 100%;
  flex: 1;
  overflow: hidden;

  margin: 8px 0 8px;

  border: 1px solid
    ${({ theme, error, success }) => {
      if (error) {
        return theme?.['-ds-error-300']?.value
      }

      if (success) {
        return theme.brand.jungle
      }

      return theme.colors.textGreyLight
    }};

  ${({ rounded }) =>
    rounded &&
    `
    border-radius: 6px;
  `}

  input {
    width: 100%;
    padding: 18px;
    border: none;
    outline: none;
  }

  .PhoneInputCountry {
    padding-left: 18px;
  }

  & > svg {
    position: absolute;

    top: 50%;
    width: 20px;

    height: 20px;
    right: 20px;

    color: #9b9b9b;

    transform: translateY(-50%);
  }
`

type CountrySelectProps = {
  value?: string
  onChange: (next: string | undefined) => void
  rounded?: boolean
  labels?: Record<string, string>
}

const CountrySelect: FC<CountrySelectProps> = ({
  value,
  onChange,
  labels = en as Record<string, string>,
  rounded,
}) => {
  const countries = getCountries()
  const preferredCountries = ['AU', 'NZ', 'US']

  return (
    <SelectField className="select-field" white rounded={rounded}>
      &nbsp;
      <select
        value={value || ''}
        onChange={(event) => onChange(event.target.value || undefined)}
        aria-label="Select country"
      >
        <option value="" disabled>
          Select country code
        </option>
        <option
          aria-label="separator"
          style={{ fontSize: '1pt', backgroundColor: '#000000' }}
          disabled
        >
          &nbsp;
        </option>
        {preferredCountries.map((country) => (
          <option key={country} value={country}>
            {labels[country]} +{getCountryCallingCode(country as any)}
          </option>
        ))}
        <option
          aria-label="separator"
          style={{ fontSize: '1pt', backgroundColor: '#000000' }}
          disabled
        >
          &nbsp;
        </option>
        {countries.map((country: string) => (
          <option key={country} value={country}>
            {labels[country]} +{getCountryCallingCode(country as any)}
          </option>
        ))}
      </select>
      <span>
        <ChevronDown style={{ width: '20px', height: 'auto' }} />
      </span>
    </SelectField>
  )
}

const useCountryState = (
  value?: string,
): [string | undefined, (next: string | undefined) => void] => {
  const [country, setCountry] = useState<string | undefined>()

  const { data } = useGetUserCountryAndStripeKeyQuery({
    fetchPolicy: 'cache-and-network',
    skip: country != null,
  })

  const userDefaultCountryCode = data?.currentUser?.defaultCountry?.code

  const setOnceFromValue = useRef(false)

  useEffect(() => {
    if (country == null && setOnceFromValue.current === false) {
      setCountry(userDefaultCountryCode || undefined)
    }
  }, [userDefaultCountryCode, country])

  useEffect(() => {
    if (setOnceFromValue.current === true) return
    let parsed
    try {
      parsed = parsePhoneNumber(value || '')
    } catch (parseError) {
      if (parseError?.message === 'TOO_SHORT') {
        // ignore
      }
    }
    if (parsed?.country) {
      setCountry(parsed.country)
      setOnceFromValue.current = true
    }
  }, [value])

  return [country, setCountry]
}

type PhoneInputProps = {
  input: { value?: string; onChange: (next: string) => void; name?: string }
  meta: { error?: string; submitFailed?: boolean }
  showAsValid?: boolean
  rounded?: boolean
  disabled?: boolean
}

export const PhoneInput: FC<PhoneInputProps> = ({
  input,
  meta: { error, submitFailed },
  showAsValid,
  rounded,
  ...custom
}) => {
  const { value, onChange, name: phoneInputName } = input
  const [country, setCountry] = useCountryState(value)

  const handleChangeCountry = (newCountry: string | undefined) => {
    onChange('')
    setCountry(newCountry)
  }

  return (
    <InputGroup noMargin>
      <CountrySelect
        value={country}
        onChange={handleChangeCountry}
        rounded={rounded}
      />
      <PhoneInputWrapper rounded={rounded} error={error && submitFailed}>
        <Input
          country={country as any}
          value={value}
          onChange={(next: any) => onChange(next || '')}
          name={phoneInputName}
          {...custom}
          aria-label="Phone number"
          disabled={!country}
        />
        {showAsValid && <ValidIcon data-testid="phone_number_valid_icon" />}
      </PhoneInputWrapper>
    </InputGroup>
  )
}

type Props = {
  name: string
  label?: ReactNode
  rounded?: boolean
  showAsValid?: boolean
  rules?: Parameters<ReturnType<typeof useFormContext>['register']>[1]
}

const PhoneNumberField: FC<Props> = ({
  name,
  label,
  rounded,
  showAsValid,
  rules,
}) => {
  const {
    formState: { errors, isSubmitted },
    control,
  } = useFormContext()
  const error = get(errors, name)

  return (
    <FieldWrapper name={name} label={label}>
      <Controller
        control={control}
        name={name}
        rules={rules as any}
        render={({ field: { value, onChange, name: fieldName } }) => (
          <PhoneInput
            input={{ value: value || '', onChange, name: fieldName }}
            meta={{
              error: (error?.message as string) || undefined,
              submitFailed: isSubmitted,
            }}
            rounded={rounded}
            showAsValid={showAsValid}
          />
        )}
      />
    </FieldWrapper>
  )
}

export default PhoneNumberField
